import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Printer, Save, Stethoscope } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function DoctorPlanner() {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [form, setForm] = useState({
    symptoms: "", onset: "", impact: "", treatments_tried: "", medications: "",
    questions: ["", "", ""], discussion_topics: [],
  });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/doctor-plan").then((r) => {
      setTopics(r.data.topics);
      if (r.data.plan) {
        const q = r.data.plan.questions || [];
        setForm({
          symptoms: r.data.plan.symptoms || "",
          onset: r.data.plan.onset || "",
          impact: r.data.plan.impact || "",
          treatments_tried: r.data.plan.treatments_tried || "",
          medications: r.data.plan.medications || "",
          questions: [q[0] || "", q[1] || "", q[2] || ""],
          discussion_topics: r.data.plan.discussion_topics || [],
        });
      }
      setLoaded(true);
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setQ = (i, v) => setForm((f) => ({ ...f, questions: f.questions.map((q, idx) => (idx === i ? v : q)) }));
  const toggleTopic = (t) =>
    setForm((f) => ({
      ...f,
      discussion_topics: f.discussion_topics.includes(t)
        ? f.discussion_topics.filter((x) => x !== t)
        : [...f.discussion_topics, t],
    }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/doctor-plan", { ...form, questions: form.questions.filter((q) => q.trim()) });
      toast.success("Your planner is saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const Field = ({ label, k, placeholder, rows = 3 }) => (
    <div>
      <label className="text-sm font-semibold text-ink">{label}</label>
      <textarea
        data-testid={`doctor-${k}`}
        value={form[k]}
        onChange={(e) => set(k, e.target.value)}
        rows={rows}
        className="mt-1.5 w-full rounded-xl border border-subtle bg-white px-4 py-3 text-ink outline-none focus:border-[var(--brand)] resize-none"
        placeholder={placeholder}
      />
    </div>
  );

  if (!loaded) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 rounded-full border-[3px] border-[var(--surface-accent)] border-t-[var(--brand)] animate-spin" /></div>;

  return (
    <div className="px-5 pt-6 animate-fade-rise print-area" data-testid="doctor-planner-page">
      <button onClick={() => navigate("/toolkit")} className="flex items-center gap-1 text-ink-muted mb-4 no-print" data-testid="doctor-back">
        <ChevronLeft size={20} /> Toolkit
      </button>

      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-sage flex items-center justify-center"><Stethoscope size={18} className="text-brand" /></div>
        <p className="overline-label">Doctor Conversation Planner</p>
      </div>
      <h1 className="font-serif text-3xl font-bold text-ink mt-2">Prepare for your appointment</h1>
      <p className="text-ink-soft text-sm mt-2">
        This helps you organise your thoughts and questions for a qualified healthcare professional. It doesn't recommend or replace treatment.
      </p>

      <div className="mt-6 space-y-4">
        <div className="bg-app-secondary rounded-2xl p-5 space-y-4">
          <Field label="Main symptoms" k="symptoms" placeholder="What are you experiencing most?" />
          <Field label="When they started" k="onset" placeholder="Roughly when did you first notice them?" rows={2} />
          <Field label="How they affect everyday life" k="impact" placeholder="Work, sleep, relationships, mood…" />
          <Field label="What you've already tried" k="treatments_tried" placeholder="Lifestyle changes, products, anything that helped or didn't" />
          <Field label="Medications / supplements (optional)" k="medications" placeholder="Anything you currently take" rows={2} />
        </div>

        <div className="bg-white rounded-2xl p-5 card-shadow">
          <label className="text-sm font-semibold text-ink">Three questions for your healthcare professional</label>
          <div className="mt-3 space-y-2.5">
            {form.questions.map((q, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-brand font-bold">{i + 1}.</span>
                <input
                  data-testid={`doctor-question-${i}`}
                  value={q}
                  onChange={(e) => setQ(i, e.target.value)}
                  className="flex-1 rounded-xl border border-subtle bg-app px-4 py-2.5 text-ink outline-none focus:border-[var(--brand)]"
                  placeholder="Your question"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 card-shadow">
          <label className="text-sm font-semibold text-ink">Topics I'd like to discuss</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {topics.map((t) => {
              const active = form.discussion_topics.includes(t);
              return (
                <button
                  key={t}
                  data-testid={`doctor-topic-${t.replace(/[^a-z]/gi, "").toLowerCase()}`}
                  onClick={() => toggleTopic(t)}
                  className={`text-sm font-medium px-4 py-2 rounded-full border-2 transition-colors ${active ? "border-[var(--brand)] bg-sage text-ink" : "border-subtle bg-white text-ink-soft"}`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div className="border border-subtle rounded-2xl p-4 bg-app-secondary no-print">
          <p className="text-xs text-ink-muted leading-relaxed">
            This planner is for preparation only. It does not provide medical advice or recommend treatments. Please discuss your options with a qualified healthcare professional, and seek urgent care for severe or concerning symptoms.
          </p>
        </div>
      </div>

      <div className="flex gap-3 mt-6 mb-4 no-print">
        <button
          data-testid="doctor-save"
          onClick={save}
          disabled={busy}
          className="flex-1 bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Save size={16} /> {busy ? "Saving…" : "Save"}
        </button>
        <button
          data-testid="doctor-print"
          onClick={() => window.print()}
          className="flex-1 border-2 border-[var(--brand)] text-brand font-semibold py-3.5 rounded-full flex items-center justify-center gap-2"
        >
          <Printer size={16} /> Print / Download
        </button>
      </div>
    </div>
  );
}
